/**
 * useBattmanDialog — Track 3 Phase 5
 *
 * Owns the Block Attribute Manager (BATTMAN) dialog feature
 * (Phase 2.4) extracted from `App.tsx` as part of the Track 3
 * App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns `battmanDialogOpen` and `battmanInitialBlock` local state.
 *   - Exposes imperative `open()` / `close()` for the command-line
 *     router (BATTMAN / МЕНАТР, also reachable via ATTSYNC fallback)
 *     and `resetShellState()`. `open(blockName?)` lets future entry
 *     points (INSERT/Properties palette) preselect a specific block.
 *   - Owns the four BATTMAN commit handlers (Edit / Remove / Reorder
 *     / Sync) — each dispatches the matching geometry editor command
 *     and emits a localized command-feed entry.
 *
 * AutoCAD parity:
 *   - `Edit` covers the dialog's Edit screen which atomically updates
 *     the attribute properties AND (optionally) renames the tag. We
 *     dispatch the two commands back-to-back; if one fails the other
 *     still lands — matches AutoCAD's partial-commit behaviour.
 *   - `Sync` is the BATTMAN ATTSYNC path; ATTSYNC can also be issued
 *     directly from the command line on a selected INSERT (the App
 *     orchestrator handles that branch — when no INSERT is selected
 *     it falls back to opening BATTMAN).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: {
 *       drawingDocument,
 *       textStyles,
 *       initialBlockName,
 *       onEdit, onRemove, onReorder, onSync, onCancel,
 *     },
 *     open, close,
 *   }
 *
 * The orchestrator mounts `<BlockAttributeManagerDialog>` when
 * `isOpen` and spreads `props`. The dialog is otherwise stateless —
 * it reads `drawingDocument` directly to populate the block / attdef
 * tables.
 */

import { useCallback, useState } from "react";

import { useDialogOpenState } from "./useDialogOpenState";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";
import type {
  BattmanEditCommit,
  BattmanRemoveCommit,
  BattmanReorderCommit,
  BattmanSyncCommit,
} from "../components/BlockAttributeManagerDialog";

export interface UseBattmanDialogParams {
  /** Receives localized command-feed entries (edited / removed / sync). */
  appendCommandFeed: (text: string) => void;
}

export interface UseBattmanDialogResult {
  /** Whether to mount `<BlockAttributeManagerDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<BlockAttributeManagerDialog>`. */
  props: {
    drawingDocument: ReturnType<
      typeof useEditorStore.getState
    >["geometryDocument"];
    textStyles: NonNullable<
      ReturnType<
        typeof useEditorStore.getState
      >["geometryDocument"]["styleTables"]
    >["textStyles"];
    initialBlockName: string | undefined;
    onEdit: (commit: BattmanEditCommit) => void;
    onRemove: (commit: BattmanRemoveCommit) => void;
    onReorder: (commit: BattmanReorderCommit) => void;
    onSync: (commit: BattmanSyncCommit) => void;
    onCancel: () => void;
  };
  /** Imperative open. Optional `blockName` preselects a block. */
  open: (blockName?: string) => void;
  /** Imperative close — used by `resetShellState()` and `onCancel`. */
  close: () => void;
}

export function useBattmanDialog({
  appendCommandFeed,
}: UseBattmanDialogParams): UseBattmanDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  const [initialBlockName, setInitialBlockName] = useState<string | undefined>(
    undefined,
  );
  const { isOpen, open: openDialog, close } = useDialogOpenState({
    onClose: () => setInitialBlockName(undefined),
  });

  const open = useCallback(
    (blockName?: string) => {
      setInitialBlockName(blockName);
      openDialog();
    },
    [openDialog],
  );

  // ── BATTMAN handlers ────────────────────────────────────────
  // Stateless one-shot dialog — four mutators dispatch the matching
  // editor commands and let the reducer handle validation / audit.
  const onEdit = useCallback(
    (commit: BattmanEditCommit) => {
      // AutoCAD's Edit dialog updates properties AND (optionally) the
      // tag in a single atomic operation. We dispatch the two commands
      // back-to-back; if the rename fails the update-attdef still lands
      // and vice-versa — matches AutoCAD's partial-commit behaviour.
      if (commit.kind === "update-attdef" || commit.kind === "both") {
        if (commit.updates) {
          // Build the updates payload that matches the
          // geometry/update-attdef contract. The dialog always sends the
          // full set of properties; we strip the tag because it is
          // handled separately via rename-attdef-tag.
          const updatesPayload: {
            prompt?: string;
            defaultValue?: string;
            height?: number;
            rotation?: number;
            widthFactor?: number;
            obliqueAngle?: number;
            styleName?: string;
            hJustification?: number;
            vJustification?: number;
            flags?: {
              invisible?: boolean;
              constant?: boolean;
              verify?: boolean;
              preset?: boolean;
              lockPosition?: boolean;
              multiline?: boolean;
            };
            backgroundFill?: boolean;
            backgroundColor?: string;
            fillBoxScale?: number;
          } = {};
          const u = commit.updates;
          if (u.prompt !== undefined) updatesPayload.prompt = u.prompt;
          if (u.defaultValue !== undefined)
            updatesPayload.defaultValue = u.defaultValue;
          if (u.height !== undefined) updatesPayload.height = u.height;
          if (u.rotation !== undefined) updatesPayload.rotation = u.rotation;
          if (u.widthFactor !== undefined)
            updatesPayload.widthFactor = u.widthFactor;
          if (u.obliqueAngle !== undefined)
            updatesPayload.obliqueAngle = u.obliqueAngle;
          if (u.styleName !== undefined) updatesPayload.styleName = u.styleName;
          if (u.hJustification !== undefined)
            updatesPayload.hJustification = u.hJustification;
          if (u.vJustification !== undefined)
            updatesPayload.vJustification = u.vJustification;
          if (u.flags) updatesPayload.flags = u.flags;
          if (u.backgroundFill !== undefined)
            updatesPayload.backgroundFill = u.backgroundFill;
          if (u.backgroundColor !== undefined)
            updatesPayload.backgroundColor = u.backgroundColor;
          if (u.fillBoxScale !== undefined)
            updatesPayload.fillBoxScale = u.fillBoxScale;

          dispatch({
            type: "geometry/update-attdef",
            source: "toolbar",
            blockName: commit.blockName,
            attdefId: commit.attdefId,
            updates: updatesPayload,
          });
        }
      }
      if (commit.kind === "rename-attdef-tag" || commit.kind === "both") {
        if (commit.newTag) {
          dispatch({
            type: "geometry/rename-attdef-tag",
            source: "toolbar",
            blockName: commit.blockName,
            attdefId: commit.attdefId,
            newTag: commit.newTag,
          });
        }
      }
      appendCommandFeed(
        t("feed.battman.edited", {
          id: commit.attdefId,
          block: commit.blockName,
        }),
      );
    },
    [dispatch, appendCommandFeed, t],
  );

  const onRemove = useCallback(
    (commit: BattmanRemoveCommit) => {
      dispatch({
        type: "geometry/remove-attdef",
        source: "toolbar",
        blockName: commit.blockName,
        attdefId: commit.attdefId,
        removeFromInserts: commit.removeFromInserts,
      });
      appendCommandFeed(
        t("feed.battman.removed", {
          id: commit.attdefId,
          block: commit.blockName,
        }),
      );
    },
    [dispatch, appendCommandFeed, t],
  );

  const onReorder = useCallback(
    (commit: BattmanReorderCommit) => {
      dispatch({
        type: "geometry/reorder-attdefs",
        source: "toolbar",
        blockName: commit.blockName,
        attdefIds: commit.attdefIds,
      });
      appendCommandFeed(
        t("feed.battman.reordered", { block: commit.blockName }),
      );
    },
    [dispatch, appendCommandFeed, t],
  );

  const onSync = useCallback(
    (commit: BattmanSyncCommit) => {
      dispatch({
        type: "geometry/attsync",
        source: "toolbar",
        blockName: commit.blockName,
      });
      appendCommandFeed(t("feed.attsync", { block: commit.blockName }));
    },
    [dispatch, appendCommandFeed, t],
  );

  return {
    isOpen,
    props: {
      drawingDocument: geometryDocument,
      textStyles: geometryDocument.styleTables?.textStyles ?? [],
      initialBlockName,
      onEdit,
      onRemove,
      onReorder,
      onSync,
      onCancel: close,
    },
    open,
    close,
  };
}
